import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {
  private scriptLoaded = false;
  private readonly siteKey = environment.recaptchaKey;

  constructor() {}

  /**
   * Dynamically loads the reCAPTCHA Enterprise script if not already loaded
   */
  loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded && (window as any).grecaptcha?.enterprise) {
        return resolve();
      }

      const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');
      if (existingScript) {
        // Wait for the existing script to be ready
        this.waitForGrecaptcha().then(() => {
          this.scriptLoaded = true;
          resolve();
        }).catch(reject);
        return;
      }

      const script = document.createElement('script');
      const url = `https://www.google.com/recaptcha/enterprise.js?render=${this.siteKey}`;
      // console.log('grecaptcha endpoint url', url);
      script.src = url;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.waitForGrecaptcha().then(() => {
          this.scriptLoaded = true;
          resolve();
        }).catch(reject);
      };
      
      script.onerror = () => reject('Failed to load reCAPTCHA script');
      document.head.appendChild(script);
    });
  }

  /**
   * Waits for grecaptcha.enterprise to be available on the window object
   */
  private waitForGrecaptcha(): Promise<void> {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max
      
      const checkGrecaptcha = () => {
        if ((window as any).grecaptcha?.enterprise) {
          resolve();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkGrecaptcha, 100);
        } else {
          reject('reCAPTCHA Enterprise failed to initialize');
        }
      };
      
      checkGrecaptcha();
    });
  }

  /**
   * Executes reCAPTCHA and returns a token
   * @param actionName The action name (e.g. 'submit') for validation
   */
  async execute(actionName: string = 'submit'): Promise<string> {
    // Action names should only contain alphanumeric characters and underscores
    if (!/^[a-zA-Z0-9_]+$/.test(actionName)) {
      throw new Error('Invalid action name format. Use only alphanumeric characters and underscores.');
    }

    // console.log('Starting reCAPTCHA execution for action:', actionName);
    // console.log('Window location:', {
    //   hostname: window.location.hostname,
    //   href: window.location.href,
    //   protocol: window.location.protocol
    // });

    // console.log('About to execute reCAPTCHA with:', {
    //   siteKey: this.siteKey,
    //   action: actionName,
    //   domain: window.location.hostname
    // });
    await this.loadScript();
    // console.log('Script loaded, checking grecaptcha availability');

    // Additional safety check with small delay
    if (!(window as any).grecaptcha?.enterprise) {
      // console.log('grecaptcha.enterprise not immediately available, waiting...');
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    return new Promise((resolve, reject) => {
      if (!(window as any).grecaptcha?.enterprise) {
        // console.error('grecaptcha.enterprise not available after waiting');
        return reject('reCAPTCHA Enterprise is not available on window');
      }

      // console.log('grecaptcha.enterprise available, calling ready()');
      
      (window as any).grecaptcha.enterprise.ready(() => {
        // console.log('grecaptcha ready, executing with site key:', this.siteKey);
        
        (window as any).grecaptcha.enterprise
          .execute(this.siteKey, { action: actionName })
          .then((token: string) => {
            // console.log('Token generated successfully', token);
            resolve(token);
          })
          .catch((err: any) => {
            console.error('Token generation failed:', err);
            reject(err);
          });
      });
    });
  }

  /**
   * Checks if reCAPTCHA is ready without executing
   */
  isReady(): boolean {
    return this.scriptLoaded && !!(window as any).grecaptcha?.enterprise;
  }

  /**
   * Resets the service state (useful for testing or reinitializing)
   */
  reset(): void {
    this.scriptLoaded = false;
    const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');
    if (existingScript) {
      existingScript.remove();
    }
  }
}